import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import { 
  Eye, 
  EyeOff, 
  User, 
  GraduationCap, 
  BookOpen, 
  CheckCircle2, 
  UserCheck, 
  ShieldCheck, 
  Layers, 
  ChevronLeft,
  Briefcase
} from 'lucide-react';
import authService from '../../services/api/auth';
import { useToast } from '../../components/common/Toast';
import Alert from '../../components/common/Alert';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendErrors, setBackendErrors] = useState({});
  const { register: registerUser, isAuthenticated } = useAuth();
  const toast = useToast();
  
  // جلب البيانات الأولية للتسجيل (المواد والصفوف)
  const { data: registerData, isLoading: dataLoading } = useQuery('register-data', 
    () => authService.getRegisterData()
  );
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
        role: 'student' // القيمة الافتراضية
    }
  });

  const password = watch('password');
  const role = watch('role');

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // منطق معالجة الإرسال (كامل وبدون أي اختصار)
  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);
    
    const submitData = {
      name: data.name,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      confirmPassword: data.confirmPassword,
      role: data.role,
      gradeId: data.gradeId,
      phone: data.role === 'teacher' ? data.phone : data.studentPhone,
      address: data.address,
      bio: data.bio,
      ...(data.role === 'student' && {
        educationSystem: data.educationSystem,
        fatherPhone: data.fatherPhone,
      }),
      ...(data.role === 'teacher' && {
        subjects: data.subjects || [],
        teacherGrades: data.teacherGrades || [],
      }),
    };

    const result = await registerUser(submitData);
    if (!result.success) {
      // Handle validation errors specifically
      if (result.error && typeof result.error === 'object' && result.error.errors) {
        // Backend validation errors - store in state for individual field display
        const validationErrors = result.error.errors;
        const errorsObject = {};
        
        validationErrors.forEach(error => {
          errorsObject[error.path] = error.msg;
        });
        
        setBackendErrors(errorsObject);
        setError('يرجى تصحيح الأخطاء الموضحة بالأحمر');
      } else {
        // General error (عرض أسفل النموذج فقط بدون toast)
        const msg = result.error?.error ?? result.error ?? 'عذراً، فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.';
        setError(typeof msg === 'string' ? msg : 'عذراً، فشل إنشاء الحساب. يرجى المحاولة مرة أخرى.');
      }
    } else {
      toast.success('مرحباً بك! تم إنشاء حسابك بنجاح، وتم إرسال رابط تفعيل إلى بريدك الإلكتروني.');
      setBackendErrors({}); // Clear errors on success
    }
    setIsLoading(false);
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-400 font-black animate-pulse uppercase tracking-widest text-xs">جاري تهيئة منصة التسجيل...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative Background Circles */}
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10"></div>

      <div className="max-w-2xl mx-auto w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        {/* Branding Header */}
        <div className="text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="bg-primary-600 p-4 rounded-[1.5rem] shadow-xl shadow-primary-200 transform group-hover:rotate-12 transition-all duration-500">
                <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div className="text-right">
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">مـنـصـتـي</h1>
                <p className="text-primary-600 text-xs font-black uppercase tracking-[0.2em] mt-1">الجيل الجديد للتعلم</p>
            </div>
          </Link>
          <div className="space-y-1">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">ابدأ رحلة النجاح اليوم</h2>
            <p className="text-gray-500 font-medium">خطوات بسيطة تفصلك عن أقوى محتوى تعليمي في الثانوية العامة</p>
          </div>
        </div>

        {/* Form Main Card */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 p-8 lg:p-12 border border-gray-100">
          
          {error && (
            <div className="mb-8">
                <Alert 
                    type="error" 
                    message={error}
                    dismissible
                    onDismiss={() => setError(null)}
                />
            </div>
          )}

          <form className="space-y-10" onSubmit={handleSubmit(onSubmit)}>
            
            {/* Step 1: Role Selection */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                <h3 className="text-lg font-black text-gray-900">أنا أسجل بصفتي:</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className={`relative flex flex-col items-center p-6 rounded-[2rem] border-2 cursor-pointer transition-all duration-300 group ${
                    role === 'student' ? 'border-primary-600 bg-primary-50/50 shadow-lg shadow-primary-100' : 'border-gray-100 bg-gray-50/50 hover:bg-white hover:border-primary-200'
                  }`}>
                  <input type="radio" value="student" {...register('role', { required: 'نوع المستخدم مطلوب' })} className="sr-only" />
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-colors ${role === 'student' ? 'bg-primary-600 text-white' : 'bg-white text-gray-400 group-hover:text-primary-500'}`}>
                    <User size={28} />
                  </div>
                  <span className={`font-black text-sm uppercase tracking-widest ${role === 'student' ? 'text-primary-700' : 'text-gray-500'}`}>تسجيل كطالب</span>
                  {role === 'student' && <CheckCircle2 className="absolute top-4 left-4 text-primary-600" size={20} />}
                </label>

                <label className={`relative flex flex-col items-center p-6 rounded-[2rem] border-2 cursor-pointer transition-all duration-300 group ${
                    role === 'teacher' ? 'border-primary-600 bg-primary-50/50 shadow-lg shadow-primary-100' : 'border-gray-100 bg-gray-50/50 hover:bg-white hover:border-primary-200'
                  }`}>
                  <input type="radio" value="teacher" {...register('role', { required: 'نوع المستخدم مطلوب' })} className="sr-only" />
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-colors ${role === 'teacher' ? 'bg-primary-600 text-white' : 'bg-white text-gray-400 group-hover:text-primary-500'}`}>
                    <Briefcase size={28} />
                  </div>
                  <span className={`font-black text-sm uppercase tracking-widest ${role === 'teacher' ? 'text-primary-700' : 'text-gray-500'}`}>تسجيل كمدرس</span>
                  {role === 'teacher' && <CheckCircle2 className="absolute top-4 left-4 text-primary-600" size={20} />}
                </label>
              </div>
              {errors.role && <p className="text-[10px] font-black text-red-500 mr-4 uppercase tracking-tighter animate-pulse">{errors.role.message}</p>}
              {backendErrors.role && <p className="text-[10px] font-black text-red-500 mr-4 uppercase tracking-tighter animate-pulse">❌ {backendErrors.role}</p>}
            </div>

            {/* Step 2: Identification Fields */}
            <div className="space-y-6 animate-in fade-in duration-500">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                    <h3 className="text-lg font-black text-gray-900">البيانات الشخصية:</h3>
                </div>

                {/* Name Fields Logic */}
                {role === 'student' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                        label="الاسم الأول"
                        {...register('firstName', { required: 'الاسم الأول مطلوب' })}
                        placeholder="محمد"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                    <Input
                        label="اسم الأب (اختياري)"
                        {...register('middleName')}
                        placeholder="عبدالله"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                    <Input
                        label="اسم العائلة"
                        {...register('lastName', { required: 'اسم العائلة مطلوب' })}
                        placeholder="عبدالمطلب"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                        label="الاسم الأول"
                        {...register('firstName', { required: 'الاسم الأول مطلوب' })}
                        placeholder="محمد"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                    <Input
                        label="اسم الأب (اختياري)"
                        {...register('middleName')}
                        placeholder="عبدالله"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                    <Input
                        label="اسم العائلة"
                        {...register('lastName', { required: 'اسم العائلة مطلوب' })}
                        placeholder="عبدالمطلب"
                        className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    />
                  </div>
                )}

                  <Input
                  label="البريد الإلكتروني"
                  type="email"
                  autoComplete="email"
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب للتوثيق',
                    pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'تنسيق البريد الإلكتروني غير صحيح',
                    },
                  })}
                  placeholder="example@email.com"
                  className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                  />
                  {errors.email && <p className="text-[10px] font-bold text-red-500 px-2">{errors.email.message}</p>}
                  {backendErrors.email && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.email}</p>}

                  {role === 'student' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم هاتف الطالب *</label>
                            <div className="relative">
                                <input
                                {...register('studentPhone', { required: 'رقم هاتف الطالب مطلوب' })}
                                type="tel"
                                className={`w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold ${
                                    errors.studentPhone ? 'border-red-300' : 'border-gray-100'
                                }`}
                                placeholder="+20 -- --- ----"
                                />
                            </div>
                            {errors.studentPhone && <p className="text-[10px] font-bold text-red-500 px-2">{errors.studentPhone.message}</p>}
                            {backendErrors.studentPhone && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.studentPhone}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم هاتف ولي الأمر *</label>
                            <div className="relative">
                                <input
                                {...register('fatherPhone', { required: 'رقم هاتف الأب مطلوب' })}
                                type="tel"
                                className={`w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold ${
                                    errors.fatherPhone ? 'border-red-300' : 'border-gray-100'
                                }`}
                                placeholder="+20 -- --- ----"
                                />
                            </div>
                            {errors.fatherPhone && <p className="text-[10px] font-bold text-red-500 px-2">{errors.fatherPhone.message}</p>}
                            {backendErrors.fatherPhone && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.fatherPhone}</p>}
                        </div>
                    </div>
                  )}

                  {role === 'teacher' && (
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم هاتف التواصل الرسمي *</label>
                        <div className="relative">
                            <input
                            {...register('phone', { required: 'رقم الهاتف مطلوب للتواصل' })}
                            type="tel"
                            className={`w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold ${
                                errors.phone ? 'border-red-300' : 'border-gray-100'
                            }`}
                            placeholder="+20 -- --- ----"
                            />
                        </div>
                        {errors.phone && <p className="text-[10px] font-bold text-red-500 px-2">{errors.phone.message}</p>}
                            {backendErrors.phone && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.phone}</p>}
                    </div>
                  )}
                </div>

            {/* Step 3: Security Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50/50 rounded-[2rem] border border-gray-100">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">كلمة المرور القوية *</label>
                    <div className="relative">
                        <input
                        {...register('password', {
                            required: 'كلمة المرور مطلوبة',
                            minLength: { value: 6, message: 'يجب ألا تقل عن 6 أحرف' },
                        })}
                        type={showPassword ? 'text' : 'password'}
                        className={`w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold ${
                            errors.password ? 'border-red-300' : 'border-gray-100'
                        }`}
                        placeholder="••••••••"
                        />
                        <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-primary-600 transition-colors"
                        >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {errors.password && <p className="text-[10px] font-bold text-red-500 px-2">{errors.password.message}</p>}
                    {backendErrors.password && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.password}</p>}
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">تأكيد كلمة المرور *</label>
                    <input
                        type="password"
                        autoComplete="new-password"
                        {...register('confirmPassword', {
                            required: 'تأكيد كلمة المرور مطلوب',
                            validate: (value) => value === password || 'كلمات المرور غير متطابقة',
                        })}
                        className={`w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold ${
                            errors.confirmPassword ? 'border-red-300' : 'border-gray-100'
                        }`}
                        placeholder="••••••••"
                    />
                    {errors.confirmPassword && <p className="text-[10px] font-bold text-red-500 px-2">{errors.confirmPassword.message}</p>}
                    {backendErrors.confirmPassword && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.confirmPassword}</p>}
                </div>
            </div>

            {/* Step 4: System Specific Data */}
            <div className="space-y-8 animate-in fade-in duration-700">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                    <h3 className="text-lg font-black text-gray-900">معلومات التخصص والدراسة:</h3>
                </div>

                {role === 'student' ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Select
                            label="الصف الدراسي"
                            {...register('gradeId', { required: 'الصف الدراسي مطلوب' })}
                            className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                            options={[
                                { value: '', label: 'اختر الصف الدراسي' },
                                ...(registerData?.grades?.map(grade => ({
                                    value: grade.id,
                                    label: grade.name
                                })) || [])
                            ]}
                        />
                        {backendErrors.gradeId && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.gradeId}</p>}

                        <Select
                            label="نظام التعليم"
                            {...register('educationSystem', { required: 'نظام التعليم مطلوب' })}
                            className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                            options={[
                                { value: '', label: 'اختر نظام التعليم' },
                                ...(registerData?.educationSystems?.map(system => ({
                                    value: system.id,
                                    label: system.name_ar
                                })) || [])
                            ]}
                        />
                        {backendErrors.educationSystem && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.educationSystem}</p>}
                    </div>
                </div>
                ) : (
                <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                                <BookOpen size={14} className="text-primary-500" /> المواد التي تقدمها
                            </label>
                            <div className="grid grid-cols-1 gap-2 bg-gray-50 p-4 rounded-[2rem] border border-gray-100 max-h-48 overflow-y-auto">
                                {registerData?.subjects?.map(subject => (
                                <label key={subject.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white transition-colors cursor-pointer group">
                                    <input type="checkbox" value={subject.id} {...register('subjects')} className="w-5 h-5 text-primary-600 rounded-lg border-gray-200 focus:ring-primary-500" />
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-primary-600 transition-colors">{subject.name}</span>
                                </label>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                                <Layers size={14} className="text-primary-500" /> الصفوف التعليمية
                            </label>
                            <div className="grid grid-cols-1 gap-2 bg-gray-50 p-4 rounded-[2rem] border border-gray-100 max-h-48 overflow-y-auto">
                                {registerData?.grades?.map(grade => (
                                <label key={grade.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white transition-colors cursor-pointer group">
                                    <input type="checkbox" value={grade.id} {...register('teacherGrades')} className="w-5 h-5 text-primary-600 rounded-lg border-gray-200 focus:ring-primary-500" />
                                    <span className="text-sm font-bold text-gray-700 group-hover:text-primary-600 transition-colors">{grade.name}</span>
                                </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Step 5: Location & Final Bio */}
            <div className="space-y-6">
                <Select
                    label="المحافظة / المدينة"
                    {...register('address', { required: 'يرجى تحديد المدينة' })}
                    className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    options={[
                        { value: '', label: 'اختر مدينتك' },
                        ...(registerData?.governorates?.map(gov => ({
                            value: gov.id,
                            label: gov.name_ar
                        })) || [])
                    ]}
                />
                {backendErrors.address && <p className="text-[10px] font-bold text-red-500 px-2 animate-pulse">❌ {backendErrors.address}</p>}

                <Textarea
                    label="نبذة تعريفية مختصرة (اختياري)"
                    {...register('bio')}
                    placeholder="اكتب نبذة عن طموحاتك العلمية أو خبرتك المهنية..."
                    className="w-full px-5 py-4 bg-white border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-bold"
                    rows={4}
                />
            </div>

            {/* Terms and Privacy Notice */}
            <p className="text-[10px] text-gray-400 text-center font-medium px-4">
                بالنقر على إنشاء الحساب، فإنك توافق على <Link to="/terms" className="text-primary-600 font-black">شروط الاستخدام</Link> و <Link to="/privacy" className="text-primary-600 font-black">سياسة الخصوصية</Link> الخاصة بالمنصة.
            </p>

            {/* Submit Button */}
            <div className="pt-4">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-5 bg-primary-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-primary-200 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                    {isLoading ? <LoadingSpinner size="small" /> : <UserCheck size={24} />}
                    إتمام إنشاء حسابي الآن
                </button>
            </div>

            {/* Footer Login Link */}
            <div className="text-center pt-4">
                <p className="text-gray-500 font-bold text-sm">
                    هل تمتلك حساباً مسبقاً؟{' '}
                    <Link to="/login" className="text-primary-600 font-black flex items-center justify-center gap-1 mt-2 hover:gap-3 transition-all">
                        تسجيل الدخول <ChevronLeft size={18} />
                    </Link>
                </p>
            </div>
          </form>
        </div>

        {/* Dynamic Support Info */}
        <div className="text-center pb-12">
            <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full border border-gray-100 shadow-sm">
                <ShieldCheck className="text-emerald-500" size={18} />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">بياناتك محمية ومشفرة بالكامل 100%</span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Register;